import { useState } from "react";
import axios from "axios";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { CONTACT_EMAIL } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = ["General", "Giving", "Volunteer", "Prayer", "Missionary Support"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", reason: "General", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete every field");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent", {
        description: "Todd will be in touch personally. Thank you for reaching out.",
      });
      setForm({ name: "", email: "", reason: "General", message: "" });
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        status === 503
          ? "Email is temporarily unavailable. Please write to Todd directly."
          : "We couldn't send that just now. Please email Todd directly."
      );
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full border-b border-navy/20 bg-transparent py-3.5 text-base text-navy outline-none transition-colors duration-400 placeholder:text-navy/35 focus:border-crimson";

  return (
    <section id="contact" data-testid="contact-section" className="bg-sand-dim py-28 lg:py-40">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Get In Touch</Eyebrow>
            <h2 className="font-display mt-7 text-[2.2rem] leading-[1.04] tracking-[-0.02em] text-navy sm:text-5xl">
              Get in touch with
              <span className="italic text-crimson"> Three Abide Ministries.</span>
            </h2>
            <div className="mt-10 space-y-5">
              <div>
                <p className="label-eyebrow text-sage">Email</p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="contact-direct-email-link"
                  className="font-display link-wipe mt-2 inline-block text-xl text-navy"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div>
                <p className="label-eyebrow text-sage">Primary Contact</p>
                <p className="font-display mt-2 text-xl text-navy">Todd Boyle</p>
              </div>
              <div>
                <p className="label-eyebrow text-sage">Phone</p>
                <a
                  href="tel:+14793671092"
                  data-testid="contact-phone-link"
                  className="font-display link-wipe mt-2 inline-block text-xl text-navy"
                >
                  (479) 367-1092
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-navy/12 bg-sand p-7 lg:p-10"
            >
              <div className="grid gap-7 sm:grid-cols-2">
                <div>
                  <label className="label-eyebrow text-navy/45" htmlFor="c-name">
                    Your name
                  </label>
                  <input
                    id="c-name"
                    data-testid="contact-name-input"
                    className={`${field} mt-2`}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-navy/45" htmlFor="c-email">
                    Email
                  </label>
                  <input
                    id="c-email"
                    type="email"
                    data-testid="contact-email-input"
                    className={`${field} mt-2`}
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@example.com"
                  />
                </div>
              </div>

              <div className="mt-9">
                <p className="label-eyebrow text-navy/45">I'm writing about</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`contact-reason-${r.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setForm((f) => ({ ...f, reason: r }))}
                      className={`rounded-full px-4 py-2 font-mono-accent text-[0.64rem] uppercase tracking-[0.16em] transition-colors duration-400 ${
                        form.reason === r
                          ? "bg-navy text-sand"
                          : "border border-navy/20 text-navy/60 hover:border-navy/50 hover:text-navy"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-9">
                <label className="label-eyebrow text-navy/45" htmlFor="c-message">
                  Message
                </label>
                <textarea
                  id="c-message"
                  rows={5}
                  data-testid="contact-message-input"
                  className={`${field} mt-2 resize-none`}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="How can we help?"
                />
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit-btn"
                className="btn-sheen mt-10 inline-flex items-center gap-2 rounded-full bg-crimson px-8 py-4 font-mono-accent text-[0.7rem] uppercase tracking-[0.2em] text-sand disabled:opacity-60"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {busy ? "Sending" : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <p
            data-testid="contact-closing-note"
            className="mt-14 max-w-3xl border-l-2 border-gold pl-6 text-base leading-[1.85] text-navy/75"
          >
            If you have questions or would like to discuss another way to give, please contact me at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-note-email-link"
              className="link-wipe text-navy"
            >
              {CONTACT_EMAIL}
            </a>{" "}
            or call or text me at{" "}
            <a
              href="tel:+14793671092"
              data-testid="contact-note-phone-link"
              className="link-wipe text-navy"
            >
              (479) 367-1092
            </a>
            .
          </p>
        </Reveal>
      </div>
    </section>
  );
};
